import React, { useCallback } from 'react';

const CLICK_TTL = 1000;
let activeButton = null;
const pendingCounts = new WeakMap();
const originalDisabled = new WeakMap();

const getButton = (target) =>
  target instanceof Element
    ? target.closest('button, [role="button"]')
    : null;

export const captureButtonRequest = (target) => {
  const button = getButton(target);
  if (!button || button.dataset.buttonGuard === 'none') return;

  activeButton = { button, expiresAt: Date.now() + CLICK_TTL };
};

export const attachButtonRequest = (config) => {
  if (!activeButton || activeButton.expiresAt < Date.now()) {
    activeButton = null;
    return config;
  }

  const { button } = activeButton;
  activeButton = null;
  const count = pendingCounts.get(button) ?? 0;
  pendingCounts.set(button, count + 1);
  if (count === 0) originalDisabled.set(button, button.disabled);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.dataset.buttonGuardLoading = 'true';
  button.classList.add('button-guard-loading');
  config.__buttonGuardTarget = button;
  return config;
};

export const releaseButtonRequest = (config) => {
  const button = config?.__buttonGuardTarget;
  if (!button) return;

  const count = (pendingCounts.get(button) ?? 1) - 1;
  if (count > 0) {
    pendingCounts.set(button, count);
    return;
  }

  pendingCounts.delete(button);
  button.disabled = originalDisabled.get(button) ?? false;
  originalDisabled.delete(button);
  button.removeAttribute('aria-busy');
  delete button.dataset.buttonGuardLoading;
  button.classList.remove('button-guard-loading');
};

/** 앱 전체의 request 버튼 클릭을 Axios 요청과 연결한다. */
export default function ButtonGuard({ children }) {
  const handleClickCapture = useCallback((event) => {
    const button = getButton(event.target);
    if (!button) return;

    if (button.dataset.buttonGuardLoading === 'true') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    captureButtonRequest(button);
  }, []);

  return <div onClickCapture={handleClickCapture}>{children}</div>;
}
